import React, { useState } from "react";
import { toast } from "sonner";
import { Cloud, ExternalLink, Link as LinkIcon, Gift } from "lucide-react";
import { safeJson } from "@/lib/api";
import { Spinner } from "@/components/Spinner";
import { CLOUDBET_REFERRAL_URL } from "@/constants/links";

const Step = ({ n, title, children }) => (
  <div className="flex gap-3.5">
    <div className="w-8 h-8 shrink-0 rounded-full bg-[#3b82f6]/20 border-[1.5px] border-[#3b82f6] flex items-center justify-center text-[#3b82f6] font-bold text-sm mt-0.5">{n}</div>
    <div className="flex-1"><p className="font-bold text-white mb-1">{title}</p>{children}</div>
  </div>
);

export default function CloudbetSetup({ apiFetch, onDone }) {
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = apiKey.trim();
    if (!trimmed) { toast.error("Please enter your Cloudbet API key"); return; }
    if (trimmed.length < 50) { toast.error("That doesn't look like a valid Cloudbet API key (long JWT)."); return; }
    setSaving(true);
    try {
      const res = await apiFetch("/api/user/profile/api-keys", {
        method: "POST", body: JSON.stringify({ cloudbet_api_key: trimmed }),
      });
      const data = await safeJson(res);
      if (data && data.success) { toast.success("Cloudbet connected"); onDone(); }
      else toast.error((data && data.detail) || "Failed to save API key");
    } catch { toast.error("Network error. Please try again."); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] overflow-y-auto">
      <div className="max-w-md mx-auto p-6">
        <div className="flex flex-col items-center py-7">
          <div className="w-22 h-22 rounded-full bg-[#3b82f6]/10 border-2 border-[#3b82f6] flex items-center justify-center mb-4" style={{ width: 88, height: 88 }}>
            <Cloud size={46} color="#3b82f6" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Automated Trading</h1>
          <p className="text-[#9ca3af] text-center text-[15px] leading-6 px-2">Connect your Cloudbet account to enable auto-betting on live tennis matches using our 10 smart strategies.</p>
        </div>

        {/* New-user referral CTA */}
        <a
          href={CLOUDBET_REFERRAL_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="cloudbet-referral-link"
          className="flex items-center gap-3 bg-[#10b981]/10 border border-[#10b981]/40 rounded-xl p-4 mb-6 hover:bg-[#10b981]/15 transition-colors"
        >
          <div className="w-9 h-9 shrink-0 rounded-full bg-[#10b981]/20 flex items-center justify-center"><Gift size={18} color="#10b981" /></div>
          <div className="flex-1">
            <p className="text-[#10b981] font-bold text-sm">New to Cloudbet?</p>
            <p className="text-[13px] text-[#9ca3af]">Create your account with our partner link to get started.</p>
          </div>
          <ExternalLink size={16} className="text-[#10b981] shrink-0" />
        </a>

        <div className="space-y-5 mb-6">
          <Step n={1} title="Open Cloudbet">
            <p className="text-sm text-[#9ca3af]">Go to cloudbet.com → Settings → API</p>
            <a href="https://www.cloudbet.com/account/settings/api" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-2 bg-[#3b82f6]/10 text-[#3b82f6] text-[13px] font-semibold px-3 py-2 rounded-lg">
              <ExternalLink size={14} /> Open Cloudbet API Settings
            </a>
          </Step>
          <Step n={2} title="Create API Key">
            <p className="text-sm text-[#9ca3af]">Click &quot;Create New API Key&quot;. Enable both Read and Trading permissions. Copy the generated key.</p>
          </Step>
          <Step n={3} title="Paste Below"><p className="text-sm text-[#9ca3af]">Paste your API key in the field below to connect.</p></Step>
        </div>

        <label className="text-sm font-semibold text-[#e2e8f0] mb-2 block">Cloudbet API Key</label>
        <textarea
          value={apiKey} onChange={(e) => setApiKey(e.target.value)} rows={3}
          placeholder="eyJhbGciOiJSUzI1NiIs..."
          data-testid="cloudbet-key-input"
          className="w-full bg-[#1e293b] border border-[#334155] rounded-xl p-3.5 text-white text-sm outline-none focus:border-[#3b82f6] mb-5"
        />

        <button onClick={save} disabled={saving} data-testid="cloudbet-save-btn" className="w-full bg-[#3b82f6] text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <Spinner size={20} color="#fff" /> : (<><LinkIcon size={20} /> Connect Cloudbet</>)}
        </button>
        <button onClick={onDone} data-testid="cloudbet-skip-btn" className="w-full text-[#6b7280] text-sm py-3 mt-2">I&apos;ll do this later</button>
      </div>
    </div>
  );
}
